import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Search,
  MessageSquare,
  Boxes,
  Workflow,
  History,
  BookOpen,
  Plug,
  Network,
  Server,
  ClipboardList,
  FileCode,
  Bookmark,
  Settings,
  Hash,
  FileSearch,
  Sparkles,
  Wand2,
} from "lucide-react";

type Cmd = {
  id: string;
  label: string;
  hint?: string;
  icon: React.ComponentType<{ className?: string }>;
  group: string;
  shortcut?: string;
  run: () => void;
};

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      setIndex(0);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  const close = () => setOpen(false);

  const commands: Cmd[] = useMemo(() => {
    const nav = (label: string, to: string, icon: Cmd["icon"]): Cmd => ({
      id: `nav-${to}`,
      label,
      hint: to,
      icon,
      group: "Navigate",
      run: () => {
        navigate({ to });
        close();
      },
    });
    return [
      nav("Copilot", "/copilot", MessageSquare),
      nav("Macro Builder", "/builder", Wand2),
      nav("Macro Catalog", "/catalog", Boxes),
      nav("Workflows", "/workflows", Workflow),
      nav("Run History", "/runs", History),
      nav("Knowledge Base", "/knowledge", BookOpen),
      nav("CAD Integrations", "/integrations", Plug),
      nav("Teamcenter Bridge", "/teamcenter", Network),
      nav("Execution Hosts", "/hosts", Server),
      nav("Audit Log", "/audit", ClipboardList),
      nav("My Macros", "/my-macros", FileCode),
      nav("Saved Workflows", "/saved-workflows", Bookmark),
      nav("Settings", "/settings", Settings),
      {
        id: "find-macro",
        label: "Find a macro by name or ID",
        hint: "Search the catalog",
        icon: Hash,
        group: "Find",
        run: close,
      },
      {
        id: "find-workflow",
        label: "Open a workflow",
        hint: "Browse workflows",
        icon: Workflow,
        group: "Find",
        run: close,
      },
      {
        id: "find-run",
        label: "Find a recent run",
        hint: "Execution history",
        icon: History,
        group: "Find",
        run: close,
      },
      {
        id: "find-kb",
        label: "Search knowledge base",
        hint: "Macro standards & patterns",
        icon: FileSearch,
        group: "Find",
        run: close,
      },
      {
        id: "ask-copilot",
        label: "Ask copilot a question…",
        hint: "Natural language",
        icon: Sparkles,
        group: "Ask Copilot",
        shortcut: "↵",
        run: close,
      },
    ];
  }, [navigate]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter(
      (c) => c.label.toLowerCase().includes(q) || c.hint?.toLowerCase().includes(q),
    );
  }, [commands, query]);

  const grouped = useMemo(() => {
    const groups: Record<string, Cmd[]> = {};
    filtered.forEach((c) => {
      (groups[c.group] ||= []).push(c);
    });
    return groups;
  }, [filtered]);

  useEffect(() => {
    setIndex(0);
  }, [query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      filtered[index]?.run();
    }
  };

  if (!open) return null;

  let cursor = -1;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]" onClick={close}>
      <div className="bg-foreground/20 absolute inset-0 backdrop-blur-sm" />
      <div
        className="border-border-strong bg-popover relative z-10 w-full max-w-xl overflow-hidden rounded-md border shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-border flex items-center gap-2 border-b px-3">
          <Search className="text-muted-foreground h-3.5 w-3.5" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search or run a command"
            className="placeholder:text-muted-foreground h-10 w-full bg-transparent text-[13px] outline-none"
          />
          <kbd className="border-border bg-muted text-muted-foreground rounded border px-1.5 py-0.5 text-[10px]">
            esc
          </kbd>
        </div>

        <div className="max-h-[360px] overflow-y-auto py-1">
          {filtered.length === 0 && (
            <div className="text-muted-foreground px-3 py-6 text-center text-[13px]">
              No results
            </div>
          )}
          {Object.entries(grouped).map(([group, items]) => (
            <div key={group} className="px-1 py-1">
              <div className="text-muted-foreground px-2 py-1 text-[10.5px] font-medium tracking-wider uppercase">
                {group}
              </div>
              {items.map((c) => {
                cursor++;
                const active = cursor === index;
                const Icon = c.icon;
                return (
                  <button
                    key={c.id}
                    onMouseEnter={() => setIndex(filtered.indexOf(c))}
                    onClick={() => c.run()}
                    className={`flex w-full items-center gap-2.5 rounded-sm px-2 py-1.5 text-left text-[13px] transition-colors ${
                      active
                        ? "bg-sidebar-active text-foreground"
                        : "text-foreground/90 hover:bg-sidebar-hover"
                    }`}
                  >
                    <Icon className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                    <span className="flex-1 truncate">{c.label}</span>
                    {c.hint && <span className="text-muted-foreground text-[11px]">{c.hint}</span>}
                    {active && c.shortcut && (
                      <kbd className="border-border bg-background text-muted-foreground rounded border px-1.5 py-0.5 text-[10px]">
                        {c.shortcut}
                      </kbd>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
